import { Component, OnInit, OnDestroy } from '@angular/core';
import { FinanceService } from '../../../services/finance.service';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';

import { saveAs } from 'file-saver';
import { SubscriptionLike as ISubscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorMessageService } from '../../../../shared/error-message.service';

declare var moment: any;

@Component({
    selector: 'app-finance-cashout',
    templateUrl: './finance-cashout.component.html',
    styleUrls: ['./finance-cashout.component.scss'],
})
export class FinanceCashoutComponent implements OnInit, OnDestroy {
    loading: boolean = true;

    querySubscriber: ISubscription;
    dataSubscriber: ISubscription;

    tableData;
    total: any = null;

    startData = {};

    filters = {};

    queryString: string = '';

    selects: any = {
        statuses: [],
        gateways: [],
        sources: []
    };

    promises = [];

    exportFile = null;
    exportLink = null;

    calendarDates = {
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
    };

    item = null;

    isSending: { [key: number]: boolean } = {};

    constructor(
        private activatedRoute: ActivatedRoute,
        private sanitizer: DomSanitizer,
        private financeService: FinanceService,
        private router: Router,
        private modalService: NgbModal,
        private errorMessageService: ErrorMessageService
    ) {}

    ngOnInit() {
        this.getStatuses();
        this.querySubscriber = this.activatedRoute.queryParams.subscribe(
            (queryParams) => {
                this.startData = {
                    created_at_start: this.calendarDates.startDate,
                    created_at_end: this.calendarDates.endDate,
                    status_id: '',
                    merchant_transaction_id: '',
                    provider_transaction_id: '',
                    provider_id: '',
                    source_id: '',
                    loan_code_str: '',
                    amount: '',
                    late_fee: '',
                    fee: '',
                    capital: '',
                    source: '',
                    source_account: '',
                    source_account_balance: ''
                };

                if (this.dataSubscriber) {
                    this.dataSubscriber.unsubscribe();
                }

                // Set Init Filters
                this.setInitFilters(queryParams);
                // Create Query String
                this.createQueryString(this.filters);

                this.getTableData();
            }
        );
    }

    ngOnDestroy() {
        this.querySubscriber.unsubscribe();
        this.dataSubscriber.unsubscribe();
    }

    getTableData() {
        this.loading = true;
        this.dataSubscriber = this.financeService
            .getCashOut(this.queryString)
            .subscribe((response) => {
                if (response) {
                    this.tableData = response;
                    this.getTotals(this.queryString);
                    this.loading = false;
                }
            });
    }

    getDataOnFilterSubmit() {
        // Set Init Filters
        this.filters = { ...this.filters, page: 0 };

        // Create Query String
        this.createQueryString(this.filters);

        // Push Query String to history.
        // Navigates without replacing the current state in history.
        this.pushFiltersToHistory(this.filters);
    }

    createQueryString(filters = {}) {
        const clearFilters = { ...filters };

        Object.keys(clearFilters).forEach(
            (key) =>
                (!clearFilters[key] || clearFilters[key] === 'empty') &&
                delete clearFilters[key]
        );

        this.queryString = Object.keys(clearFilters)
            .map((key) => key + '=' + clearFilters[key])
            .join('&');
    }

    pushFiltersToHistory(filters, replaceUrl = false) {
        Object.keys(filters).forEach(
            (key) => !filters[key] && delete filters[key]
        );

        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: Object.keys(filters).length > 0 ? filters : {},
            replaceUrl: replaceUrl,
        });
    }

    setInitFilters(query = {}) {
        this.filters = { ...this.startData, ...query };
    }

    getDataOnPageChange(page) {
        this.filters['page'] = page;

        // Create Query String
        this.createQueryString(this.filters);

        // Push Query String to history.
        // Navigates without replacing the current state in history.
        this.pushFiltersToHistory(this.filters);
    }

    onChangeDate(e: any) {
        this.filters = { ...this.filters, ...e, page: 0 };
        // Create Query String
        this.createQueryString(this.filters);

        // Push Query String to history.
        // Navigates without replacing the current state in history.
        this.pushFiltersToHistory(this.filters);
    }

    goto(lead_id) {
        if (lead_id) {
            this.router.navigate([
                `/backoffice/loans/overview/${lead_id}/loanInfo/`,
                lead_id,
            ]);
        }
    }

    getExportLink(e) {
        e.preventDefault();

        this.financeService
            .getCashOutExport(this.queryString)
            .subscribe((exel) => {
                let file = new Blob([exel], {
                    type: 'application/vnd.ms-excel',
                });
                this.exportFile = URL.createObjectURL(file);
                this.exportLink = this.sanitizer.bypassSecurityTrustUrl(
                    this.exportFile
                );

                saveAs(file, 'cahs_out.xlsx');
            });
    }

    getTotals(filterUrlParams) {
        this.financeService
            .getCashoutTotals(filterUrlParams)
            .toPromise()
            .then((response) => {
                console.log(response);
                if (response) {
                    this.total = response;
                }
            });
    }

    openPayment(payment, item) {
        this.item = { ...item};
        console.log(this.item);
        this.modalService.open(payment, { size: 'lg' });
    }

    sendCashout(item: any, type: string) {
        if (!item.loan_id) {
            this.errorMessageService.showError('Loan ID not found');
            return;
        }

        this.isSending[item.id] = true;

        const request = type === 'manually' 
            ? this.financeService.emitLoanManually(item.loan_id)
            : this.financeService.emitLoan(item.loan_id);

        request.subscribe(
            (response: any) => {
                this.isSending[item.id] = false;
                this.errorMessageService.showSuccess('Cashout sent successfully');
                this.getTableData();
            },
            error => {
                this.isSending[item.id] = false;
                this.errorMessageService.showError(error?.error?.error?.show_message || 'Failed to send cashout');
            }
        );
    }

    getStatuses() {
        this.promises.push(
            this.financeService
                .getCashoutStatuses()
                .toPromise()
                .then(response => {
                    if (response.length) {
                        this.selects.statuses = response;
                    }
                })
        );
    }

    downloadCashoutCertificate(e, issueId, namePdf): void
    {
        e.preventDefault();

        this.financeService
            .getDownloadCashoutCertificate(issueId)
            .subscribe((pdf) => {
                let file = new Blob([pdf], {
                    type: 'application/pdf',
                });
                this.exportFile = URL.createObjectURL(file);
                this.exportLink = this.sanitizer.bypassSecurityTrustUrl(
                    this.exportFile
                );

                saveAs(file, namePdf);
            });
    }
}
